/**
 * PEStart â€” GrÃ¼ndungs-Extras Store.
 * Stammdaten (Totpunkt, UL, Personal) kommen live aus Calc/Privatkostenkompass â€” hier nur Extras.
 */

import { create } from 'zustand';
import { generateSimpleId } from './utils';

// +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++
// TYPES
// +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++

export type FoundingType = 'NEW' | 'TAKEOVER';

export type SubsidyType = 'ARBEITSAGENTUR' | 'PRAEMIE' | 'DARLEHEN' | 'SONSTIGE';

export type Subsidy = {
    id: string;
    type: SubsidyType;
    label: string;
    amount: number;
    /** Monate mit FÃ¶rderwirkung (typisch 1â€“6) */
    monthsCovered: number;
    notes?: string;
};

export type SubsidiesState = {
    items: Subsidy[];
};

export type InvestmentTargetType = 'NEW' | 'TAKEOVER' | 'BOTH';

export type InvestmentItem = {
    id: string;
    category: string;
    label: string;
    amount: number;
    targetType: InvestmentTargetType;
    notes?: string;
};

export type InvestmentsState = {
    erstausstattung: number;
    umbau: number;
    marketing: number;
    kaufpreis: number;
    uebernahmekosten: number;
    sanierungInventar: number;
    items: InvestmentItem[];
};

/** Pro Mitarbeiter aus calc.personalData (und ggf. unprod/Azubi) */
export type StaffFlag = {
    fromTakeover: boolean;
    startMonth: number; // 1â€“6
};

export type StaffFlagsMap = Record<string, StaffFlag>;

/**
 * Geplanter GrÃ¼ndungskredit: Nutzer wÃ¤hlt Laufzeit + Zinssatz,
 * Monatsrate wird als AnnuitÃ¤t berechnet (kein manueller Tilgungsbetrag).
 */
export type LoanPlan = {
    /** Abzahlungsdauer in Jahren (z. B. 10 oder 15) */
    laufzeitJahre: number;
    /** Effektivzins p.a. in % (pauschal, planerisch) */
    zinssatzProzent: number;
    /**
     * Anlauf-LiquiditÃ¤tspuffer in Monaten (Fixkosten + Unternehmerlohn).
     * Konservativ eher niedrig ansetzen â€” FÃ¶rderungen kommen separat.
     */
    pufferMonate: number;
};

/** Moderate Default-Annahme (frÃ¼her 3 â€” oft zu hoch ohne Investitionen) */
export const DEFAULT_ANLAUF_PUFFER_MONATE = 2;

export const ANLAUF_PUFFER_OPTIONS = [0, 1, 2, 3] as const;

export type PEStartGeneratedSection = {
    id: string;
    title: string;
    text: string;
};

export type PEStartState = {
    foundingType: FoundingType;
    subsidies: Subsidy[];
    investments: InvestmentsState;
    staffFlags: StaffFlagsMap;
    loanPlan: LoanPlan;
    selectedPlanungsrechnerSzenarioId?: string | null;
    /** Persistierte KI-Kapitel des aktuellen Plans */
    generatedSections: PEStartGeneratedSection[];
    /** Nur PEStart/Businessplan â€” nicht Privatkostenkompass */
    privateAltersvorsorge: number;
    /** Nur PEStart/Businessplan â€” monatliche private RÃ¼cklage */
    liquiditaetspufferMonat: number;
    fachlicheQualifikation: string;
    berufserfahrung: string;
    kaufmaennischeEignung: string;
    /** Leitfragen / Notizen fÃ¼r ausfÃ¼hrliche Executive Summary */
    executiveSummaryNotes: string;
};

export type PEStartActions = {
    setFoundingType: (type: FoundingType) => void;
    setSelectedPlanungsrechnerSzenarioId: (id: string | null) => void;
    updateSubsidies: (data: Partial<SubsidiesState>) => void;
    addSubsidy: (item: Omit<Subsidy, 'id'> & { id?: string }) => void;
    removeSubsidy: (id: string) => void;
    updateInvestments: (data: Partial<InvestmentsState>) => void;
    removeInvestmentItem: (id: string) => void;
    updateLoanPlan: (patch: Partial<LoanPlan>) => void;
    setStaffFlag: (employeeId: string, patch: Partial<StaffFlag>) => void;
    ensureStaffFlags: (employeeIds: string[], defaultFromTakeover?: boolean) => void;
    /** Stellt einen Archiv-Snapshot als Arbeitsstand wieder her */
    loadPEStartState: (state: Partial<PEStartState>) => void;
    resetPEStartStore: () => void;
    /** Session: welcher Archiv-Eintrag gerade geladen ist (null = Neu/ohne Archiv) */
    setActiveArchivId: (id: string | null) => void;
    /** Neuen Businessplan: Defaults + kein Archiv-Bezug */
    startNewBusinessPlan: () => void;
    /** Archiv-Eintrag laden und als aktiv markieren */
    openArchivEntry: (id: string, state: Partial<PEStartState>) => void;
    setGeneratedSections: (sections: PEStartGeneratedSection[]) => void;
    clearGeneratedSections: () => void;
    setPrivateAltersvorsorge: (value: number) => void;
    setLiquiditaetspufferMonat: (value: number) => void;
    setFachlicheQualifikation: (value: string) => void;
    setBerufserfahrung: (value: string) => void;
    setKaufmaennischeEignung: (value: string) => void;
    setExecutiveSummaryNotes: (notes: string) => void;
};

export type PEStartStore = PEStartState &
    PEStartActions & {
        activeArchivId: string | null;
    };

// +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++
// DEFAULTS / HELPERS
// +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++

const createDefaultInvestments = (): InvestmentsState => ({
    erstausstattung: 0,
    umbau: 0,
    marketing: 0,
    kaufpreis: 0,
    uebernahmekosten: 0,
    sanierungInventar: 0,
    items: [],
});

/** Typische Hausbank-/KfW-nahe Planungsannahme fÃ¼r ExistenzgrÃ¼ndung */
export const DEFAULT_LOAN_ZINSSATZ_PROZENT = 5.5;

/** VorschlÃ¤ge fÃ¼r Kreditlaufzeit (Jahre) */
export const LOAN_LAUFZEIT_OPTIONS = [5, 7, 10, 12, 15, 20] as const;

export const createDefaultLoanPlan = (): LoanPlan => ({
    laufzeitJahre: 10,
    zinssatzProzent: DEFAULT_LOAN_ZINSSATZ_PROZENT,
    pufferMonate: DEFAULT_ANLAUF_PUFFER_MONATE,
});

export const createDefaultPEStartState = (): PEStartState => ({
    foundingType: 'NEW',
    subsidies: [],
    investments: createDefaultInvestments(),
    staffFlags: {},
    loanPlan: createDefaultLoanPlan(),
    selectedPlanungsrechnerSzenarioId: null,
    generatedSections: [],
    privateAltersvorsorge: 0,
    liquiditaetspufferMonat: 0,
    fachlicheQualifikation: '',
    berufserfahrung: '',
    kaufmaennischeEignung: '',
    executiveSummaryNotes: '',
});

/** Normalisiert KI-Kapitel aus Archiv/Storage. */
export function normalizeGeneratedSections(raw: unknown): PEStartGeneratedSection[] {
    if (!Array.isArray(raw)) return [];
    return raw
        .filter((s) => s && typeof s === 'object')
        .map((s: any) => ({
            id: String(s.id || 'section'),
            title: String(s.title || ''),
            text: String(s.text || ''),
        }))
        .filter((s) => s.text.trim().length > 0 || s.title.trim().length > 0);
}

function clampEuroMonat(v: unknown): number {
    const n = typeof v === 'number' ? v : Number(String(v ?? '').replace(',', '.'));
    if (!Number.isFinite(n) || n < 0) return 0;
    return Math.round(n * 100) / 100;
}

/** Liest KfW-Felder inkl. Legacy `gruenderProfil` aus Archiv-Snapshots. */
export function resolveKfWFieldsFromState(incoming: any): Pick<
    PEStartState,
    | 'privateAltersvorsorge'
    | 'liquiditaetspufferMonat'
    | 'fachlicheQualifikation'
    | 'berufserfahrung'
    | 'kaufmaennischeEignung'
    | 'executiveSummaryNotes'
> {
    const legacy = incoming?.gruenderProfil && typeof incoming.gruenderProfil === 'object'
        ? incoming.gruenderProfil
        : {};
    return {
        privateAltersvorsorge: clampEuroMonat(incoming?.privateAltersvorsorge),
        liquiditaetspufferMonat: clampEuroMonat(incoming?.liquiditaetspufferMonat),
        fachlicheQualifikation: String(
            incoming?.fachlicheQualifikation ?? legacy.fachlicheQualifikation ?? ''
        ),
        berufserfahrung: String(incoming?.berufserfahrung ?? legacy.berufserfahrung ?? ''),
        kaufmaennischeEignung: String(
            incoming?.kaufmaennischeEignung ?? legacy.kaufmaennischeEignung ?? ''
        ),
        executiveSummaryNotes: String(incoming?.executiveSummaryNotes ?? ''),
    };
}

function clampLoanJahre(v: unknown): number {
    const n = typeof v === 'number' ? v : parseInt(String(v), 10);
    if (!Number.isFinite(n)) return 10;
    return Math.min(30, Math.max(1, Math.round(n)));
}

function clampZinssatz(v: unknown): number {
    const n = typeof v === 'number' ? v : Number(String(v).replace(',', '.'));
    if (!Number.isFinite(n) || n < 0) return DEFAULT_LOAN_ZINSSATZ_PROZENT;
    return Math.min(20, Math.round(n * 100) / 100);
}

function clampPufferMonate(v: unknown): number {
    const n = typeof v === 'number' ? v : Number(String(v).replace(',', '.'));
    if (!Number.isFinite(n) || n < 0) return DEFAULT_ANLAUF_PUFFER_MONATE;
    return Math.min(6, Math.round(n * 2) / 2);
}

/**
 * AnnuitÃ¤tendarlehen: konstante Monatsrate aus Kreditbetrag, Laufzeit und Zinssatz p.a.
 */
export function computeLoanAnnuitaet(
    kreditbetrag: number,
    laufzeitJahre: number,
    zinssatzProzent: number
) {
    const principal = Number.isFinite(kreditbetrag) && kreditbetrag > 0 ? kreditbetrag : 0;
    const jahre = clampLoanJahre(laufzeitJahre);
    const zins = clampZinssatz(zinssatzProzent);
    const monate = Math.max(1, jahre * 12);
    const r = zins / 100 / 12;

    if (principal <= 0) {
        return {
            kreditbetrag: 0,
            laufzeitJahre: jahre,
            zinssatzProzent: zins,
            monate,
            rateMonat: 0,
            zinsAnteilMonat1: 0,
            tilgungAnteilMonat1: 0,
            gesamtzahlung: 0,
            gesamtzins: 0,
        };
    }

    let rateMonat: number;
    if (r === 0) {
        rateMonat = principal / monate;
    } else {
        const factor = Math.pow(1 + r, monate);
        rateMonat = (principal * (r * factor)) / (factor - 1);
    }

    const zinsAnteilMonat1 = principal * r;
    const tilgungAnteilMonat1 = Math.max(0, rateMonat - zinsAnteilMonat1);
    const gesamtzahlung = rateMonat * monate;
    const gesamtzins = Math.max(0, gesamtzahlung - principal);

    return {
        kreditbetrag: principal,
        laufzeitJahre: jahre,
        zinssatzProzent: zins,
        monate,
        rateMonat,
        zinsAnteilMonat1,
        tilgungAnteilMonat1,
        gesamtzahlung,
        gesamtzins,
    };
}

function upsertById<T extends { id: string }>(
    existing: T[],
    incoming: Array<Partial<T> & { id: string }>
): T[] {
    if (!incoming || incoming.length === 0) return existing;
    const next = [...existing];
    for (const patch of incoming) {
        if (!patch?.id) continue;
        const idx = next.findIndex((row) => row.id === patch.id);
        if (idx >= 0) {
            next[idx] = { ...next[idx], ...patch, id: patch.id };
        } else {
            next.push(patch as T);
        }
    }
    return next;
}

export function getInvestmentSums(inv: InvestmentsState) {
    const newFixed = (inv.erstausstattung || 0) + (inv.umbau || 0) + (inv.marketing || 0);
    const takeoverFixed =
        (inv.kaufpreis || 0) + (inv.uebernahmekosten || 0) + (inv.sanierungInventar || 0);
    const itemsNew = (inv.items || [])
        .filter((i) => i.targetType === 'NEW' || i.targetType === 'BOTH')
        .reduce((s, i) => s + (i.amount || 0), 0);
    const itemsTakeover = (inv.items || [])
        .filter((i) => i.targetType === 'TAKEOVER' || i.targetType === 'BOTH')
        .reduce((s, i) => s + (i.amount || 0), 0);
    return {
        newFixed,
        takeoverFixed,
        itemsNew,
        itemsTakeover,
        newTotal: newFixed + itemsNew,
        takeoverTotal: takeoverFixed + itemsTakeover,
        itemsTotal: (inv.items || []).reduce((s, i) => s + (i.amount || 0), 0),
    };
}

function clampStartMonth(v: unknown): number {
    const n = typeof v === 'number' ? v : parseInt(String(v), 10);
    if (!Number.isFinite(n)) return 1;
    return Math.min(6, Math.max(1, Math.round(n)));
}

// +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++
// STORE
// +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++

const usePEStartStore = create<PEStartStore>()((set, get) => ({
    ...createDefaultPEStartState(),
    activeArchivId: null,

    setFoundingType: (type) => {
        if (type !== 'NEW' && type !== 'TAKEOVER') return;
        set({ foundingType: type });
    },

    setSelectedPlanungsrechnerSzenarioId: (id) => {
        set({ selectedPlanungsrechnerSzenarioId: id || null });
    },

    updateSubsidies: (data) => {
        if (!data || typeof data !== 'object') return;
        if (data.items) {
            set({ subsidies: upsertById(get().subsidies, data.items) });
        }
    },

    addSubsidy: (item) => {
        const id = item.id || generateSimpleId();
        const row: Subsidy = {
            id,
            type: item.type,
            label: item.label ?? '',
            amount: item.amount ?? 0,
            monthsCovered: item.monthsCovered ?? 6,
            ...(item.notes !== undefined ? { notes: item.notes } : {}),
        };
        set({ subsidies: upsertById(get().subsidies, [row]) });
    },

    removeSubsidy: (id) => {
        set({ subsidies: get().subsidies.filter((s) => s.id !== id) });
    },

    updateInvestments: (data) => {
        if (!data || typeof data !== 'object') return;
        const prev = get().investments;
        const { items, ...scalars } = data;
        set({
            investments: {
                ...prev,
                ...scalars,
                items: items ? upsertById(prev.items, items) : prev.items,
            },
        });
    },

    removeInvestmentItem: (id) => {
        const prev = get().investments;
        set({
            investments: {
                ...prev,
                items: prev.items.filter((i) => i.id !== id),
            },
        });
    },

    updateLoanPlan: (patch) => {
        if (!patch || typeof patch !== 'object') return;
        const prev = get().loanPlan || createDefaultLoanPlan();
        set({
            loanPlan: {
                laufzeitJahre:
                    patch.laufzeitJahre !== undefined
                        ? clampLoanJahre(patch.laufzeitJahre)
                        : prev.laufzeitJahre,
                zinssatzProzent:
                    patch.zinssatzProzent !== undefined
                        ? clampZinssatz(patch.zinssatzProzent)
                        : prev.zinssatzProzent,
                pufferMonate:
                    patch.pufferMonate !== undefined
                        ? clampPufferMonate(patch.pufferMonate)
                        : clampPufferMonate(
                              prev.pufferMonate ?? DEFAULT_ANLAUF_PUFFER_MONATE
                          ),
            },
        });
    },

    setStaffFlag: (employeeId, patch) => {
        if (!employeeId) return;
        const prev = get().staffFlags[employeeId] || {
            fromTakeover: get().foundingType === 'TAKEOVER',
            startMonth: 1,
        };
        const next: StaffFlag = {
            fromTakeover:
                patch.fromTakeover !== undefined ? !!patch.fromTakeover : prev.fromTakeover,
            startMonth:
                patch.startMonth !== undefined
                    ? clampStartMonth(patch.startMonth)
                    : prev.startMonth,
        };
        set({
            staffFlags: {
                ...get().staffFlags,
                [employeeId]: next,
            },
        });
    },

    ensureStaffFlags: (employeeIds, defaultFromTakeover) => {
        const ids = (employeeIds || []).filter(Boolean);
        if (ids.length === 0) return;
        const flags = { ...get().staffFlags };
        const fallbackTakeover =
            defaultFromTakeover !== undefined
                ? !!defaultFromTakeover
                : get().foundingType === 'TAKEOVER';
        let changed = false;
        for (const id of ids) {
            if (!flags[id]) {
                flags[id] = { fromTakeover: fallbackTakeover, startMonth: 1 };
                changed = true;
            }
        }
        if (changed) set({ staffFlags: flags });
    },

    resetPEStartStore: () => {
        set({ ...createDefaultPEStartState(), activeArchivId: null });
    },

    setActiveArchivId: (id) => {
        set({ activeArchivId: id || null });
    },

    startNewBusinessPlan: () => {
        set({ ...createDefaultPEStartState(), activeArchivId: null });
    },

    openArchivEntry: (id, state) => {
        get().loadPEStartState(state);
        set({ activeArchivId: id || null });
    },

    setGeneratedSections: (sections) => {
        set({
            generatedSections: normalizeGeneratedSections(sections),
        });
    },

    clearGeneratedSections: () => {
        set({ generatedSections: [] });
    },

    setPrivateAltersvorsorge: (value) => {
        set({ privateAltersvorsorge: clampEuroMonat(value) });
    },

    setLiquiditaetspufferMonat: (value) => {
        set({ liquiditaetspufferMonat: clampEuroMonat(value) });
    },

    setFachlicheQualifikation: (value) => {
        set({ fachlicheQualifikation: String(value ?? '') });
    },

    setBerufserfahrung: (value) => {
        set({ berufserfahrung: String(value ?? '') });
    },

    setKaufmaennischeEignung: (value) => {
        set({ kaufmaennischeEignung: String(value ?? '') });
    },

    setExecutiveSummaryNotes: (notes) => {
        set({ executiveSummaryNotes: String(notes ?? '') });
    },

    loadPEStartState: (incoming) => {
        if (!incoming || typeof incoming !== 'object') return;
        const defaults = createDefaultPEStartState();
        const loanIn = incoming.loanPlan || defaults.loanPlan;
        const kfw = resolveKfWFieldsFromState(incoming);
        set({
            foundingType:
                incoming.foundingType === 'TAKEOVER' || incoming.foundingType === 'NEW'
                    ? incoming.foundingType
                    : defaults.foundingType,
            subsidies: Array.isArray(incoming.subsidies) ? incoming.subsidies : defaults.subsidies,
            investments: {
                ...defaults.investments,
                ...(incoming.investments || {}),
                items: Array.isArray(incoming.investments?.items)
                    ? incoming.investments!.items
                    : defaults.investments.items,
            },
            staffFlags:
                incoming.staffFlags && typeof incoming.staffFlags === 'object'
                    ? incoming.staffFlags
                    : defaults.staffFlags,
            loanPlan: {
                laufzeitJahre: clampLoanJahre(loanIn.laufzeitJahre),
                zinssatzProzent: clampZinssatz(loanIn.zinssatzProzent),
                pufferMonate: clampPufferMonate(
                    loanIn.pufferMonate ?? DEFAULT_ANLAUF_PUFFER_MONATE
                ),
            },
            selectedPlanungsrechnerSzenarioId:
                typeof incoming.selectedPlanungsrechnerSzenarioId === 'string' &&
                incoming.selectedPlanungsrechnerSzenarioId.trim()
                    ? incoming.selectedPlanungsrechnerSzenarioId.trim()
                    : null,
            generatedSections: normalizeGeneratedSections(
                (incoming as Partial<PEStartState>).generatedSections
            ),
            ...kfw,
        });
    },
}));

export default usePEStartStore;